import { ListTenantMembersParams, TenantMember, TenantMemberDisplayData } from '../identity/types'
import { MediaObject, MediaObjectUrl } from '../media'
import {
  BatchItem,
  BatchSearch,
  BatchedAction,
  ExecuteBatchedAction,
  PreviewBatchedAction,
} from '../shared'
import {
  Audience,
  DateTimeString,
  PageResult,
  PaginatedSearch,
  WithPermissions,
} from '../shared/types'

export type RewardProviderEmbeds =
  | 'audience'
  | 'description'
  | 'tangoProvider'
  | 'customRewardProvider'

export enum RewardProviderType {
  GRS = 'GRS',
  TANGO = 'tango',
  CUSTOM = 'custom',
}

export type RewardProvider = WithPermissions<{
  id: string
  type: RewardProviderType
  name: string
  tenantId: string
  teamIds: string[] | []
  tenantWide: boolean
  enabled: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  audience?: Audience
  description?: string
  __entity_type__: 'rewardProvider'
  // embeds
  tangoProvider?: TangoRewardProvider
  customRewardProvider?: CustomRewardProvider
}>

export enum RedemptionStatus {
  ORDERING = 'ordering',
  ORDERED = 'ordered',
  COMPLETED = 'completed',
  REFUNDING = 'refunding',
  REFUNDED = 'refunded',
  DECLINED = 'declined',
}

export type RewardProviderParams = {
  name: string
  tenantWide: boolean
  teamIds: string[]
}

export type RedemptionEmbeds = 'member' | 'rewardProvider' | 'rewardProviderName'
export type MyRedemptionEmbeds = 'rewardProviderName'

export type RedemptionFilters = {
  idIn?: string[]
  providerIdIn?: string[]
  providerRefIn?: string[]
  statusIn?: RedemptionStatus[]
  typeIn?: string[]
  ownerIdIn?: string[]
  createdInTimeSpan?: string[]
  teamIdIn?: string[]
  dateRangePreset?: string
}

export type ListAllRedemptionsParams = PaginatedSearch<RedemptionFilters, RedemptionEmbeds>
export type ListMyRedemptionsParams = PaginatedSearch<object, MyRedemptionEmbeds>

export type TangoRewardFilters = {
  countryCodeIn?: string[]
  fixedAmount?: boolean
  categoryIdIn?: string[]
}

export type TangoRewardEmbeds = 'displayData' | 'rewards'

export type GetRewardsProviderParams = {
  embeds: RewardProviderEmbeds[]
}

export type ListTangoRewardsParams = PaginatedSearch<TangoRewardFilters, TangoRewardEmbeds>

export type ListRewardProvidersParams = PaginatedSearch<{ idIn?: string[] }, RewardProviderEmbeds>

export type Redemption = {
  id: string
  providerId: string
  tenantId: string
  ownerId: string
  type: string
  pointsAmount: number
  providerRef: string
  displayName: string
  currency: string
  quantity: number
  clientCostCents: number
  status: RedemptionStatus
  pointsMultiplier: number
  createdAt: string
  updatedAt: string
  // embeds
  memberDisplayData?: TenantMemberDisplayData
  rewardProvider?: RewardProvider
  rewardProviderName?: string
  __entity_type__: 'redemption'
}

export type SsoUrl = {
  url: string
}

export type RewardType = {
  type: string
}

export type CreateCreditCardParams = {
  number: string
  expirationDate: string
  verificationNumber: string
  firstName: string
  lastName: string
  addressLine1: string
  city: string
  state: string
  postalCode: string
  country: string
}

export enum TangoCreditCardStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum TangoRewardStatus {
  ACTIVE = 'active',
  DELETED = 'deleted',
  INACTIVE = 'inactive',
  TEST = 'test',
}

export type TangoCreditCard = {
  id: string
  tangoProviderId: string
  createdAt: string
  lastSyncedAt: string
  status: TangoCreditCardStatus
  name: string
  activatedAt: string
  __entity_type__: 'tangoCreditCard'
  // embeds
  displayData: {
    lastFourDigits: string
    expirationDate: string
    contacts: string[]
  }
}

export type FundTangoAccountParams = {
  tangoCreditCardId: string
  amountCents: number
  verificationNumber: string
}

export type CreditCardEmbeds = 'displayData'

export type ListCreditCardParams = PaginatedSearch<
  { status?: TangoCreditCardStatus },
  CreditCardEmbeds
>

export enum CurrencyCode {
  USD = 'USD',
  CAD = 'CAD',
  GBP = 'GBP',
  EUR = 'EUR',
  AED = 'AED',
  AFN = 'AFN',
  ALL = 'ALL',
  AMD = 'AMD',
  ANG = 'ANG',
  AOA = 'AOA',
  ARS = 'ARS',
  AUD = 'AUD',
  AWG = 'AWG',
  AZN = 'AZN',
  BAM = 'BAM',
  BBD = 'BBD',
  BDT = 'BDT',
  BGN = 'BGN',
  BHD = 'BHD',
  BIF = 'BIF',
  BMD = 'BMD',
  BND = 'BND',
  BOB = 'BOB',
  BRL = 'BRL',
  BSD = 'BSD',
  BTC = 'BTC',
  BTN = 'BTN',
  BWP = 'BWP',
  BYN = 'BYN',
  BZD = 'BZD',
  CDF = 'CDF',
  CHF = 'CHF',
  CLF = 'CLF',
  CLP = 'CLP',
  CNH = 'CNH',
  CNY = 'CNY',
  COP = 'COP',
  CRC = 'CRC',
  CUC = 'CUC',
  CUP = 'CUP',
  CVE = 'CVE',
  CZK = 'CZK',
  DJF = 'DJF',
  DKK = 'DKK',
  DOP = 'DOP',
  DZD = 'DZD',
  EGP = 'EGP',
  ERN = 'ERN',
  ETB = 'ETB',
  FJD = 'FJD',
  FKP = 'FKP',
  GEL = 'GEL',
  GGP = 'GGP',
  GHS = 'GHS',
  GIP = 'GIP',
  GMD = 'GMD',
  GNF = 'GNF',
  GTQ = 'GTQ',
  GYD = 'GYD',
  HKD = 'HKD',
  HNL = 'HNL',
  HRK = 'HRK',
  HTG = 'HTG',
  HUF = 'HUF',
  IDR = 'IDR',
  ILS = 'ILS',
  IMP = 'IMP',
  INR = 'INR',
  IQD = 'IQD',
  IRR = 'IRR',
  ISK = 'ISK',
  JEP = 'JEP',
  JMD = 'JMD',
  JOD = 'JOD',
  JPY = 'JPY',
  KES = 'KES',
  KGS = 'KGS',
  KHR = 'KHR',
  KMF = 'KMF',
  KPW = 'KPW',
  KRW = 'KRW',
  KWD = 'KWD',
  KYD = 'KYD',
  KZT = 'KZT',
  LAK = 'LAK',
  LBP = 'LBP',
  LKR = 'LKR',
  LRD = 'LRD',
  LSL = 'LSL',
  LYD = 'LYD',
  MAD = 'MAD',
  MDL = 'MDL',
  MGA = 'MGA',
  MKD = 'MKD',
  MMK = 'MMK',
  MNT = 'MNT',
  MOP = 'MOP',
  MRU = 'MRU',
  MUR = 'MUR',
  MVR = 'MVR',
  MWK = 'MWK',
  MXN = 'MXN',
  MYR = 'MYR',
  MZN = 'MZN',
  NAD = 'NAD',
  NGN = 'NGN',
  NIO = 'NIO',
  NOK = 'NOK',
  NPR = 'NPR',
  NZD = 'NZD',
  OMR = 'OMR',
  PAB = 'PAB',
  PEN = 'PEN',
  PGK = 'PGK',
  PHP = 'PHP',
  PKR = 'PKR',
  PLN = 'PLN',
  PYG = 'PYG',
  QAR = 'QAR',
  RON = 'RON',
  RSD = 'RSD',
  RUB = 'RUB',
  RWF = 'RWF',
  SAR = 'SAR',
  SBD = 'SBD',
  SCR = 'SCR',
  SDG = 'SDG',
  SEK = 'SEK',
  SGD = 'SGD',
  SHP = 'SHP',
  SLL = 'SLL',
  SOS = 'SOS',
  SRD = 'SRD',
  SSP = 'SSP',
  STD = 'STD',
  STN = 'STN',
  SVC = 'SVC',
  SYP = 'SYP',
  SZL = 'SZL',
  THB = 'THB',
  TJS = 'TJS',
  TMT = 'TMT',
  TND = 'TND',
  TOP = 'TOP',
  TRY = 'TRY',
  TTD = 'TTD',
  TWD = 'TWD',
  TZS = 'TZS',
  UAH = 'UAH',
  UGX = 'UGX',
  UYU = 'UYU',
  UZS = 'UZS',
  VES = 'VES',
  VND = 'VND',
  VUV = 'VUV',
  WST = 'WST',
  XAF = 'XAF',
  XAG = 'XAG',
  XAU = 'XAU',
  XCD = 'XCD',
  XDR = 'XDR',
  XOF = 'XOF',
  XPD = 'XPD',
  XPF = 'XPF',
  XPT = 'XPT',
  YER = 'YER',
  ZAR = 'ZAR',
  ZMW = 'ZMW',
  ZWL = 'ZWL',
}

export type TangoProviderEmbeds = 'rewardProvider' | 'owners'

export type TangoRewardProvider = WithPermissions<{
  id: string
  tenantId: string
  customerIdentifier: string
  balanceCents: number
  currencyCode: CurrencyCode
  status: TangoRewardStatus
  lowBalanceThresholdCents: number
  pointValueCents: number
  lastSyncedAt: string
  rewardProviderId: string
  balanceNotifiedAt: string | null
  disclaimer: string
  ownerIds: string[]
  createdAt: string
  updatedAt: string
  __entity_type__: 'tangoProvider'
  // embeds
  rewardProvider?: RewardProvider & { currencyCode: CurrencyCode }
  owners?: TenantMemberDisplayData[]
}>

export type TangoDepositsEmbeds = 'member' | 'creditCard'

export type ListTangoDepositsParams = PaginatedSearch<object, TangoDepositsEmbeds>

export enum TangoDepositStatus {
  SUCCESS = 'success',
  PENDING = 'pending',
  DECLINED = 'declined',
}

export type TangoDeposit = {
  id: string
  tangoProviderId: string
  createdAt: string
  lastSyncedAt: string
  status: TangoDepositStatus
  amountCents: number
  chargedAmountCents: number
  memberId: string
  tangoCreditCardId: string
  __entity_type__: 'tangoDeposit'
  // embeds
  memberDisplayData?: TenantMemberDisplayData
  creditCard?: TangoCreditCard
}

export type RewardProviderRedemptionSummary = {
  type: 'yearToDate' | 'monthToDate'
  data: {
    points: number
    cents: number
  }
}

export type CreateTangoProviderParams = {
  currencyCode: CurrencyCode
} & UpdateTangoProviderParams

export type UpdateTangoProviderParams = {
  name: string
  tenantWide: boolean
  pointValueCents: number
  lowBalanceThresholdCents: number
  teamIds: string[]
  disclaimer: string
  ownerIds: string[]
  enabled: boolean
}

export type LastRedeemTangoRewardEmbeds = 'displayData'

export enum TangoBrandStatus {
  ACTIVE = 'active',
  DELETED = 'deleted',
  INACTIVE = 'inactive',
  TEST = 'test',
}

export type TangoReward = {
  id: string
  name: string
  tangoBrandId: string
  rewardType: string
  status: TangoRewardStatus
  currencyCode: CurrencyCode
  faceValueCents: number | null
  minValueCents: number
  maxValueCents: number
  wholeAmount: boolean
  countryCodes: string[]
  createdAt: string
  lastSyncedAt: string
  __entity_type__: 'tangoReward'
  // embeds
  displayData?: {
    instructionsHtml: string
  }
}

export type TangoBrand = {
  id: string
  name: string
  catergoryIds: string[]
  status: TangoBrandStatus
  createdAt: string
  lastSyncedAt: string
  __entity_type__: 'tangoBrand'
  // embeds
  displayData?: {
    termsHtml: string
    imageUrl: string
  }
  rewards?: TangoReward[]
}

export type TangoCategory = {
  id: string
  name: string
  lastSyncedAt: string
  __entity_type__: 'TangoCategory'
}

export type RedeemTangoRewardParams = {
  tangoRewardId: string
  amountCents: number
}

export type ListRewardOwnersResult = {
  tenantMembers: TenantMember[]
}

export type ListRewardOwnersParams = Pick<
  ListTenantMembersParams,
  'idIn' | 'suspended' | 'search' | 'roleIn'
>

export type RewardEstimateParams = {
  amountCents?: number
  availablePoints?: number
}

export type RewardEstimate = {
  amountCents: number
  points: number
}

// Custom rewards
export type CustomRewardProvider = WithPermissions<{
  id: string
  rewardProviderId: string
  tenantId: string
  disclaimer: string
  createdAt: string
  updatedAt: string
  embeds: CustomRewardProviderEmbeds[]
  pendingFulfillmentCount: number
  __entity_type__: 'customRewardProvider'
  // rewardProviderEmbed
  rewardProvider?: RewardProvider
}>

type CustomRewardProviderSummaryStat = {
  current: number
  diff: {
    percent: number
    value: number
  }
}

export type CustomRewardProviderSummary = {
  count: CustomRewardProviderSummaryStat
  points: CustomRewardProviderSummaryStat
}

export type CreateOrUpdateCustomRewardProviderParams = {
  name: string
  tenantWide: boolean
  teamIds: string[]
  disclaimer: string
  enabled: boolean
}

export type CustomRewardProviderEmbeds = 'rewardProvider'

export type CustomRewardVariant = WithPermissions<{
  id: string
  customRewardId: string
  available: null | number
  lowStockThreshold: number | null
  lowStockNotifiedAt: string | null
  archivedAt: string | null
  createdAt: string
  updatedAt: string
  __entity_type__: 'customRewardVariant'
}>

export type CustomReward = WithPermissions<{
  id: string
  tenantId: string
  rewardProviderId: string
  name: string
  description: string
  mediaObject: MediaObject
  mediaObjectId: string
  points: number
  valueCents: number | null
  valueCurrency: string | null
  schedulingEnabled: boolean
  startAt: DateTimeString
  endAt: DateTimeString | null
  stockOwnerIds: string[]
  fulfillmentOwnerIds: string[]
  lastFeaturedAt: string | null
  limit: number | null
  limitPeriod: RedemptionLimitPeriodEnum | null
  archivedAt: string | null
  redemptionInfo: string
  updatedAt: string
  createdAt: string
  __entity_type_: 'customReward'
  // embeds
  variants?: CustomRewardVariant[]
  status?: CustomRewardActiveStatusEnum
  stockOwners?: TenantMemberDisplayData[]
  fulfillmentOwners?: TenantMemberDisplayData[]
  mediaUrls?: MediaObjectUrl
  orderHistory?: {
    lastRedeemedAt: string | null
    nextRedeemableAt: string | null
  }
  pendingOrderCount?: number
  completedOrderCount?: number
}>

export enum CustomRewardActiveStatusEnum {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SCHEDULED = 'scheduled',
}

export enum RedemptionLimitPeriodEnum {
  WEEK = 'week',
  MONTH = 'month',
  QUATER = 'quarter',
  YEAR = 'year',
}

export type CreateCustomRewardParams = {
  name: string
  description: string
  mediaObjectId: string
  points: number
  valueCents: number | null
  valueCurrency: string | null
  schedulingEnabled: boolean
  startAt?: DateTimeString
  endAt?: DateTimeString | null
  active?: boolean
  fulfillmentOwnerIds: string[]
  available: number | null
  limit: number | null
  limitPeriod: RedemptionLimitPeriodEnum | null
  redemptionInfo: string
  featured: boolean
  lowStockThreshold: number | null
  stockOwnerIds: string[]
}

export type UpdateCustomRewardParams = Omit<CreateCustomRewardParams, 'available'> & {
  available?: number | null
}

export type CustomRewardEmbeds = 'variants' | 'status' | 'owners' | 'mediaUrls' | 'orderCounts'

export type CustomRewardFilters = {
  statusIn?: CustomRewardActiveStatusEnum[]
}

export type ListCustomRewardsParams = PaginatedSearch<CustomRewardFilters, CustomRewardEmbeds>

export type ScheduleCustomRewardParams = {
  schedulingEnabled: boolean
  startAt?: DateTimeString
  endAt?: DateTimeString
  active?: boolean
}

// User facing store rewards
export type CustomRewardStoreProviderEmbeds = 'disclaimer'

export type CustomRewardStoreProvider = WithPermissions<{
  id: string
  tenantId: string
  createdAt: string
  updatedAt: string
  embeds: CustomRewardStoreProviderEmbeds[]
  pendingFulfillmentCount: number
  __entity_type__: 'customRewardProvider'
  // disclaimerEmbed
  disclaimer?: string
}>
export type CustomRewardStoreEmbeds = 'mediaUrls' | 'variants' | 'orderHistory' | 'status'

export type ListCustomRewardStoreRewardsParams = PaginatedSearch<object, CustomRewardStoreEmbeds>

export type SendRedemptionTestEmailParams = {
  name: string
  redemptionInfo?: string
}

export type CustomRewardRedemptionStat = {
  type: 'redemptions'
  data: {
    current: number
    previous: number
    diff: {
      value: number
      percent: number | null
    }
  }
}

export type AdjustCustomRewardInventoryParams = {
  available: number
}

export enum CustomRewardOrderStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  CANCELED = 'canceled',
}

export type CustomRewardOrderEmbeds = 'member' | 'statusUpdatedBy'

export type CustomRewardOrder = WithPermissions<{
  id: string
  customRewardVariantId: string
  tenantId: string
  memberId: string
  points: number
  status: CustomRewardOrderStatus
  statusUpdatedById: string | null
  statusUpdatedAt: string | null
  createdAt: string
  __entity_type__: 'customRewardOrder'
  //embeds
  memberDisplayData?: TenantMemberDisplayData
  statusUpdatedBy?: TenantMemberDisplayData
}>

export type CustomRewardOrderFilters = {
  createdInTimeSpan?: string[]
  statusIn?: CustomRewardOrderStatus[]
  memberIdIn?: string[]
  customRewardIdIn?: string[]
}

export type ListCustomRewardOrderParams = PaginatedSearch<
  CustomRewardOrderFilters,
  CustomRewardOrderEmbeds
>

export type CompleteCustomRewardOrderParams = {
  notify: boolean
  additionalInfo: string
}

export type SendCompleteOrderTestEmailParams = {
  additionalInfo: string
}

export type BatchCompleteOrdersFiltersParams = BatchSearch<{
  statusIn?: CustomRewardActiveStatusEnum[]
  customRewardIdIn?: string[]
}>

export type BatchCompleteOrdersBulkParams = {
  init: BatchedAction<
    { sorting: string[] },
    BatchCompleteOrdersFiltersParams | { idIn: string[] },
    { batchId: string }
  >
  preview: PreviewBatchedAction<
    { page: number; pageSize: number },
    PageResult<BatchItem<CustomRewardOrder, unknown, unknown>>
  >
  execute: ExecuteBatchedAction<CompleteCustomRewardOrderParams>
}

export type CancelCustomRewardOrderParams = {
  notify: boolean
  additionalInfo: string
}
